/**
 * ArchetypeMocks — iter-212. The 16 mock components that complete the
 * coverage of `VISUAL_KEYS` from `ArchetypeFeatureVisual.jsx`. Each
 * one is a focused, static React component (no network, no demo data)
 * that visualises ONE specific feature for its archetype.
 *
 * Design rules
 * ------------
 * - Lumfi palette (emerald/sky/purple/amber/rose). No archetype-specific
 *   tinting — the user asked to keep the current palette consistent.
 * - Pure presentational; no hooks beyond styling.
 * - Each mock owns its own `data-testid` so the A/B framework can
 *   measure exposures per concrete variant.
 * - Numbers/labels are deliberately freelancer-flavoured so a visitor
 *   to /for/:slug sees data that matches their world.
 */
import {
  TrendingUp, TrendingDown, Calendar as CalendarIcon, Target, Coins,
  CheckCircle2, Clock, AlertTriangle, AlertCircle, Mail, FileText, Bell,
  ShieldCheck, MessageCircle, BarChart3, ArrowDownRight, ArrowUpRight,
  UserMinus, Repeat2, Wallet, ShoppingBag, Camera, Activity,
} from "lucide-react";

/* ──────────────────────────────────────────────────────────────── */
/* designer / coach-tutor / photographer · GoalRing                  */
/* ──────────────────────────────────────────────────────────────── */
export function GoalRingMock() {
  const pct = 68;
  const dash = (pct / 100) * 2 * Math.PI * 38;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 flex items-center gap-5" data-testid="mock-goal-ring">
      <div className="relative h-24 w-24 flex-shrink-0">
        <svg viewBox="0 0 100 100" className="h-24 w-24 -rotate-90">
          <circle cx="50" cy="50" r="38" fill="none" stroke="#e2e8f0" strokeWidth="9" />
          <circle cx="50" cy="50" r="38" fill="none" stroke="#10b981" strokeWidth="9" strokeLinecap="round"
            strokeDasharray={`${dash} 999`} />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <div className="font-display text-xl font-bold text-slate-900 tabular-nums leading-none">{pct}%</div>
            <div className="text-[9px] uppercase tracking-wider text-slate-500">Avance</div>
          </div>
        </div>
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">Meta · Mes flaco</div>
        <div className="font-display text-base font-semibold text-slate-900 mt-0.5">Reserva agosto</div>
        <div className="flex items-baseline gap-1 mt-1">
          <span className="font-display text-2xl font-bold tabular-nums text-slate-900">$ 5.4M</span>
          <span className="text-xs text-slate-500">/ 8M COP</span>
        </div>
        <div className="text-[11px] text-emerald-700 font-medium mt-1 flex items-center gap-1">
          <TrendingUp className="h-3 w-3" /> Vas adelante · faltan 26 días
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────── */
/* content-creator / lawyer / photographer · ReserveChip             */
/* ──────────────────────────────────────────────────────────────── */
export function ReserveChipMock() {
  return (
    <div className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50/70 to-white p-5" data-testid="mock-reserve-chip">
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-700 flex items-center gap-1">
        <ShieldCheck className="h-3 w-3" /> Reserva fiscal · automática
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className="font-display text-3xl font-bold tabular-nums text-slate-900">25%</span>
        <span className="text-xs text-slate-500">de cada cobro grande</span>
      </div>
      <div className="mt-4 space-y-1.5">
        {[
          { c: "Brand Deal · Logitech", g: "$ 4.8M", r: "$ 1.2M" },
          { c: "Patreon · marzo", g: "$ 2.1M", r: "$ 525K" },
          { c: "YouTube AdSense · Q1", g: "$ 6.4M", r: "$ 1.6M" },
        ].map((r, i) => (
          <div key={i} className="flex items-center justify-between text-xs bg-white border border-amber-100 rounded-lg px-3 py-2">
            <div className="min-w-0 truncate font-medium text-slate-800">{r.c}</div>
            <div className="flex items-center gap-3 text-[11px] flex-shrink-0">
              <span className="text-slate-500 tabular-nums">{r.g}</span>
              <span className="font-bold text-amber-700 tabular-nums">→ {r.r}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-3 text-[11px] text-slate-500 leading-snug">
        Lumfi aparta en una &quot;cuenta mental&quot; de impuestos por cada ingreso. Cuando llega abril ya está separado.
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────── */
/* content-creator / consultant · SourceSplit                        */
/* ──────────────────────────────────────────────────────────────── */
export function SourceSplitMock() {
  const sources = [
    { name: "YouTube AdSense", pct: 42, amount: "$ 6.4M", color: "bg-rose-500" },
    { name: "Patreon", pct: 28, amount: "$ 4.2M", color: "bg-amber-500" },
    { name: "Brand deals", pct: 22, amount: "$ 3.3M", color: "bg-emerald-500" },
    { name: "Cursos online", pct: 8, amount: "$ 1.2M", color: "bg-sky-500" },
  ];
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="mock-source-split">
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">Ingresos · Por fuente</div>
      <div className="font-display text-base font-semibold text-slate-900 mt-0.5">Q1 2026 · 15.1M COP</div>
      <div className="mt-3 h-3 w-full rounded-full overflow-hidden bg-slate-100 flex">
        {sources.map((s) => (
          <div key={s.name} className={s.color} style={{ width: `${s.pct}%` }} title={`${s.name} ${s.pct}%`} />
        ))}
      </div>
      <ul className="mt-3 space-y-1.5">
        {sources.map((s) => (
          <li key={s.name} className="flex items-center gap-2 text-xs">
            <span className={`h-2.5 w-2.5 rounded-full ${s.color} flex-shrink-0`} />
            <span className="text-slate-700 flex-1 truncate">{s.name}</span>
            <span className="font-semibold text-slate-900 tabular-nums">{s.amount}</span>
            <span className="text-slate-400 tabular-nums w-8 text-right">{s.pct}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────── */
/* consultant / lawyer · ExportRows                                  */
/* ──────────────────────────────────────────────────────────────── */
export function ExportRowsMock() {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="mock-export-rows">
      <div className="px-4 pt-4 pb-2 border-b border-slate-100 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">Reporte · Cliente</div>
          <div className="font-display text-sm font-semibold text-slate-900 mt-0.5">Marzo · GitLab Enterprise</div>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[10px] uppercase font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">CSV</span>
          <span className="text-[10px] uppercase font-bold text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-2 py-0.5">PDF</span>
          <span className="text-[10px] uppercase font-bold text-sky-700 bg-sky-50 border border-sky-200 rounded-full px-2 py-0.5">XLSX</span>
        </div>
      </div>
      <div className="divide-y divide-slate-100">
        {[
          { d: "2026-03-05", c: "Anticipo proyecto", a: "$ 8.500.000", t: "Ingreso" },
          { d: "2026-03-12", c: "Hito 1 · Discovery", a: "$ 4.200.000", t: "Ingreso" },
          { d: "2026-03-22", c: "Hosting + tools", a: "−$ 320.000", t: "Gasto" },
          { d: "2026-03-28", c: "Hito 2 · Build", a: "$ 6.800.000", t: "Ingreso" },
        ].map((r, i) => (
          <div key={i} className="px-4 py-2 grid grid-cols-[80px_1fr_auto] gap-2 text-xs items-center">
            <span className="font-mono text-slate-500 tabular-nums">{r.d}</span>
            <span className="text-slate-800 truncate">{r.c}</span>
            <span className={`tabular-nums font-semibold ${r.t === "Gasto" ? "text-rose-700" : "text-emerald-700"}`}>{r.a}</span>
          </div>
        ))}
      </div>
      <div className="px-4 py-2 bg-slate-50 text-[11px] flex items-center justify-between border-t border-slate-100">
        <span className="text-slate-500">Total neto marzo</span>
        <span className="font-bold text-slate-900 tabular-nums">$ 19.180.000</span>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────── */
/* designer · WeeklyChart                                            */
/* ──────────────────────────────────────────────────────────────── */
export function WeeklyChartMock() {
  const bars = [
    { w: "S26", v: 32 },
    { w: "S27", v: 65 },
    { w: "S28", v: 48 },
    { w: "S29", v: 90 },
    { w: "S30", v: 55 },
  ];
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="mock-weekly-chart">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">Cash-flow · semanal</div>
          <div className="font-display text-base font-semibold text-slate-900 mt-0.5">Próximas 5 semanas</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase text-slate-500">Total</div>
          <div className="font-display text-lg font-bold text-slate-900 tabular-nums">$ 18.4M</div>
        </div>
      </div>
      <div className="mt-4 flex items-end gap-2 h-28">
        {bars.map((b) => (
          <div key={b.w} className="flex-1 flex flex-col items-center gap-1">
            <div className="w-full bg-emerald-100 rounded-t-md relative overflow-hidden" style={{ height: `${b.v}%` }}>
              <div className="absolute inset-x-0 bottom-0 h-full bg-gradient-to-t from-emerald-500 to-emerald-400" />
            </div>
            <span className="text-[10px] font-mono text-slate-500">{b.w}</span>
          </div>
        ))}
      </div>
      <div className="mt-3 text-[11px] text-slate-500 leading-snug">
        Cuánto cash entra por semana — proyectado con probabilidad de cobro.
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────── */
/* designer · Timeline (project milestones)                          */
/* ──────────────────────────────────────────────────────────────── */
export function TimelineMock() {
  const milestones = [
    { d: "1 abr", t: "Anticipo · 40%", a: "$ 4.8M", st: "done" },
    { d: "20 abr", t: "Hito 1 · Wireframes", a: "$ 3.6M", st: "done" },
    { d: "12 may", t: "Hito 2 · UI screens", a: "$ 3.6M", st: "soon" },
    { d: "10 jun", t: "Cierre · Handoff", a: "$ 4.8M", st: "future" },
  ];
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="mock-timeline">
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">Proyecto · Hitos</div>
      <div className="font-display text-base font-semibold text-slate-900 mt-0.5">Rebrand Startup Y · 4 pagos</div>
      <div className="mt-4 space-y-2 relative pl-5 before:absolute before:left-2 before:top-1 before:bottom-1 before:w-px before:bg-slate-200">
        {milestones.map((m, i) => {
          const dotCls = m.st === "done"
            ? "bg-emerald-500 border-emerald-500"
            : m.st === "soon" ? "bg-white border-amber-500" : "bg-white border-slate-300";
          return (
            <div key={i} className="relative">
              <span className={`absolute -left-5 top-1 h-3 w-3 rounded-full border-2 ${dotCls}`} />
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-xs font-semibold text-slate-900 truncate">{m.t}</div>
                  <div className="text-[10px] text-slate-500">{m.d}</div>
                </div>
                <span className={`tabular-nums text-sm font-bold flex-shrink-0 ${m.st === "done" ? "text-slate-400 line-through" : "text-slate-900"}`}>{m.a}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────── */
/* content-creator · WhatsappDigest                                  */
/* ──────────────────────────────────────────────────────────────── */
export function WhatsappDigestMock() {
  return (
    <div className="rounded-2xl border border-slate-200 bg-[#E5DDD5] p-3" data-testid="mock-whatsapp-digest">
      <div className="flex items-center gap-2 mb-3 px-1">
        <div className="h-7 w-7 rounded-full bg-emerald-600 text-white flex items-center justify-center">
          <MessageCircle className="h-4 w-4" />
        </div>
        <div>
          <div className="text-xs font-semibold text-slate-800">Lumfi · Resumen</div>
          <div className="text-[10px] text-slate-500">Lun · 8:00 AM</div>
        </div>
      </div>
      <div className="rounded-2xl rounded-tl-md bg-white p-3 text-xs text-slate-800 space-y-1.5 shadow-sm max-w-[280px]">
        <div className="font-bold text-emerald-800">Hola Maya 👋 esta semana puedes usar $ 720.000.</div>
        <div className="text-slate-700">• 3 cobros confirmados: Patreon, YouTube y Logitech.</div>
        <div className="text-slate-700">• 1 atrasado: Cliente Y ($ 480K).</div>
        <div className="flex items-center gap-1 text-amber-700 font-semibold pt-1 border-t border-slate-100 mt-1">
          <AlertCircle className="h-3 w-3" /> Reserva fiscal sube a 22% en abril.
        </div>
        <div className="text-[10px] text-slate-400 pt-1">↗ Ver detalle en Lumfi</div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────── */
/* consultant · ScenarioWhatIf                                       */
/* ──────────────────────────────────────────────────────────────── */
export function ScenarioWhatIfMock() {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="mock-scenario-whatif">
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">Escenarios · What-if 90 días</div>
      <div className="font-display text-base font-semibold text-slate-900 mt-0.5">Pierdes el retainer ancla</div>
      <div className="mt-3 grid grid-cols-3 gap-2">
        {[
          { l: "Base", v: "$ 28M", color: "text-slate-700" },
          { l: "Sin retainer", v: "$ 13M", color: "text-rose-700" },
          { l: "Δ Riesgo", v: "−54%", color: "text-rose-700 font-bold" },
        ].map((m) => (
          <div key={m.l} className="rounded-xl border border-slate-200 px-3 py-2 text-center bg-slate-50">
            <div className="text-[9px] uppercase tracking-wider text-slate-500">{m.l}</div>
            <div className={`font-display text-base font-bold tabular-nums mt-1 ${m.color}`}>{m.v}</div>
          </div>
        ))}
      </div>
      <div className="mt-3 h-16 flex items-end gap-0.5">
        {Array.from({ length: 24 }).map((_, i) => {
          const baseH = 50 + Math.sin(i / 3) * 14;
          const stressH = Math.max(8, baseH - i * 2);
          return (
            <div key={i} className="flex-1 flex flex-col gap-px relative">
              <div className="bg-slate-200 rounded-t-sm" style={{ height: `${baseH}%` }} />
              <div className="absolute bottom-0 inset-x-0 bg-rose-400/80 rounded-t-sm" style={{ height: `${stressH}%` }} />
            </div>
          );
        })}
      </div>
      <div className="mt-2 flex items-center gap-3 text-[10px]">
        <span className="flex items-center gap-1"><span className="h-2 w-2 bg-slate-300 rounded-sm" />Base</span>
        <span className="flex items-center gap-1"><span className="h-2 w-2 bg-rose-400 rounded-sm" />Escenario estresado</span>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────── */
/* lawyer-accountant · ForecastChart                                 */
/* ──────────────────────────────────────────────────────────────── */
export function ForecastChartMock() {
  const points = [50, 58, 54, 62, 70, 65, 73, 80, 76, 84, 90, 88];
  const path = points.map((p, i) => `${i === 0 ? "M" : "L"} ${i * 30},${100 - p}`).join(" ");
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="mock-forecast-chart">
      <div className="flex items-start justify-between mb-1">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">Forecast · 90 días</div>
          <div className="font-display text-base font-semibold text-slate-900 mt-0.5">Saldo proyectado</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase text-slate-500">en 90 d</div>
          <div className="font-display text-lg font-bold text-emerald-700 tabular-nums">$ 42.8M</div>
        </div>
      </div>
      <svg viewBox="0 0 330 100" className="mt-3 w-full h-28">
        <defs>
          <linearGradient id="g-forecast" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#10b981" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={`${path} L 330,100 L 0,100 Z`} fill="url(#g-forecast)" />
        <path d={path} fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" />
        {points.map((p, i) => (
          <circle key={i} cx={i * 30} cy={100 - p} r="2" fill="#10b981" />
        ))}
      </svg>
      <div className="mt-2 flex items-center justify-between text-[10px] text-slate-500 font-mono">
        <span>jun</span><span>jul</span><span>ago</span><span>sep</span>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────── */
/* coach-tutor · MrrMetric (already exists as Roadmap stub —          */
/* this is the new "real" mock variant)                              */
/* ──────────────────────────────────────────────────────────────── */
export function MrrMetricMock() {
  return (
    <div className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50/60 to-white p-5" data-testid="mock-mrr-metric">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">MRR · Neto proyectado</div>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="font-display text-3xl font-bold tabular-nums text-slate-900">$ 12.640.000</span>
            <span className="text-xs text-slate-500">COP/mes</span>
          </div>
          <div className="mt-1 flex items-center gap-1 text-xs text-emerald-700 font-semibold">
            <TrendingUp className="h-3 w-3" /> +8% vs feb · después de fees + FX
          </div>
        </div>
        <div className="h-10 w-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
          <Repeat2 className="h-5 w-5" />
        </div>
      </div>
      <div className="mt-4 grid grid-cols-3 gap-2 text-center">
        {[
          { l: "Activos", v: "42" },
          { l: "Nuevos", v: "+6", c: "text-emerald-700" },
          { l: "Bajas", v: "−2", c: "text-rose-600" },
        ].map((m) => (
          <div key={m.l} className="rounded-lg bg-white border border-slate-200/70 p-2">
            <div className="text-[9px] uppercase tracking-wider text-slate-500">{m.l}</div>
            <div className={`font-display text-sm font-bold tabular-nums mt-0.5 ${m.c || "text-slate-900"}`}>{m.v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────── */
/* coach-tutor · ChurnAlert                                          */
/* ──────────────────────────────────────────────────────────────── */
export function ChurnAlertMock() {
  return (
    <div className="rounded-2xl border border-rose-200 bg-gradient-to-br from-rose-50/60 to-white p-5" data-testid="mock-churn-alert">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-2">
          <div className="h-9 w-9 rounded-full bg-rose-100 text-rose-700 flex items-center justify-center flex-shrink-0">
            <UserMinus className="h-4 w-4" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-rose-700">Churn · últimos 30 d</div>
            <div className="font-display text-2xl font-bold tabular-nums text-rose-700 mt-1 leading-none">−$ 1.470.000</div>
            <div className="text-[11px] text-slate-500 mt-1">3 cancelaciones · impacto 90 días</div>
          </div>
        </div>
      </div>
      <div className="mt-3 space-y-1">
        {[
          { c: "C. García", d: "Hace 3 días", v: "−$ 480K" },
          { c: "L. Pérez · Mensual", d: "Hace 1 día", v: "−$ 390K" },
          { c: "Startup X · plan Pro", d: "Hoy", v: "−$ 600K" },
        ].map((r, i) => (
          <div key={i} className="flex items-center justify-between bg-white border border-rose-100 rounded-lg px-3 py-2 text-xs">
            <div className="min-w-0">
              <div className="font-semibold text-slate-900 truncate">{r.c}</div>
              <div className="text-[10px] text-slate-500">{r.d}</div>
            </div>
            <span className="font-bold text-rose-700 tabular-nums">{r.v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────── */
/* photographer · MonthlyAllocation                                  */
/* ──────────────────────────────────────────────────────────────── */
export function MonthlyAllocationMock() {
  const months = [
    { m: "ene", v: 2.4 }, { m: "feb", v: 3.1 }, { m: "mar", v: 4.8 }, { m: "abr", v: 5.6 },
    { m: "may", v: 7.2 }, { m: "jun", v: 8.4 }, { m: "jul", v: 6.5 }, { m: "ago", v: 5.1 },
    { m: "sep", v: 4.2 }, { m: "oct", v: 9.8 }, { m: "nov", v: 11.2 }, { m: "dic", v: 12.6 },
  ];
  const max = 12.6;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="mock-monthly-allocation">
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">Reparto · Por mes</div>
      <div className="font-display text-base font-semibold text-slate-900 mt-0.5">Ingreso anual 2026 · $ 81.0M</div>
      <div className="mt-4 grid grid-cols-12 gap-1 items-end h-24">
        {months.map((m) => (
          <div key={m.m} className="flex flex-col items-center gap-1 h-full">
            <div className="w-full bg-emerald-100 rounded-t-md relative overflow-hidden flex-1">
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-emerald-500 to-emerald-400"
                style={{ height: `${(m.v / max) * 100}%` }} />
            </div>
            <span className="text-[9px] font-mono text-slate-500 uppercase">{m.m}</span>
          </div>
        ))}
      </div>
      <div className="mt-3 text-[11px] text-slate-500 leading-snug">
        Cada evento sumado al mes correcto. Sabes cuánto te toca &quot;teórico&quot; mensual y dónde están los meses flacos.
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────── */
/* photographer · MilestonePayments                                  */
/* ──────────────────────────────────────────────────────────────── */
export function MilestonePaymentsMock() {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="mock-milestone-payments">
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">Cobros · Anticipo + Saldo</div>
      <div className="font-display text-base font-semibold text-slate-900 mt-0.5">Boda López-García · 18 oct</div>
      <div className="mt-4 grid grid-cols-2 gap-3">
        {[
          { l: "Anticipo · 50%", a: "$ 4.000.000", d: "Pagado · 12 jun", done: true },
          { l: "Saldo · 50%", a: "$ 4.000.000", d: "Cobra · 18 oct", done: false },
        ].map((p, i) => (
          <div key={i} className={`rounded-xl border p-3 ${p.done ? "border-emerald-200 bg-emerald-50/60" : "border-slate-200 bg-slate-50"}`}>
            <div className="flex items-center gap-1.5 text-[10px] uppercase font-bold">
              {p.done ? <CheckCircle2 className="h-3 w-3 text-emerald-700" /> : <Clock className="h-3 w-3 text-slate-500" />}
              <span className={p.done ? "text-emerald-700" : "text-slate-500"}>{p.l}</span>
            </div>
            <div className="font-display text-xl font-bold tabular-nums text-slate-900 mt-1">{p.a}</div>
            <div className="text-[10px] text-slate-500 mt-0.5">{p.d}</div>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center justify-between text-xs">
        <span className="text-slate-500">Total evento</span>
        <span className="font-bold text-slate-900 tabular-nums">$ 8.000.000</span>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────── */
/* ecommerce · PlatformAccounts                                      */
/* ──────────────────────────────────────────────────────────────── */
export function PlatformAccountsMock() {
  const accounts = [
    { p: "Mercado Libre", a: "$ 12.4M", icon: ShoppingBag, fee: "8%", color: "text-amber-700 bg-amber-50 border-amber-200" },
    { p: "Shopify · USD", a: "$ 4.250 USD", icon: ShoppingBag, fee: "2.9% + 30¢", color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
    { p: "Tienda física", a: "$ 2.1M", icon: Wallet, fee: "0%", color: "text-slate-700 bg-slate-50 border-slate-200" },
  ];
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="mock-platform-accounts">
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">Cuentas · Por plataforma</div>
      <div className="font-display text-base font-semibold text-slate-900 mt-0.5">Consolidado · 32.2M COP equivalente</div>
      <div className="mt-3 space-y-2">
        {accounts.map((acc) => {
          const Icon = acc.icon;
          return (
            <div key={acc.p} className={`flex items-center gap-3 rounded-xl border px-3 py-2 ${acc.color}`}>
              <Icon className="h-4 w-4 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold truncate">{acc.p}</div>
                <div className="text-[10px] opacity-80">Comisión: {acc.fee}</div>
              </div>
              <span className="font-bold tabular-nums text-sm flex-shrink-0">{acc.a}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────── */
/* ecommerce · CommittedPayments                                     */
/* ──────────────────────────────────────────────────────────────── */
export function CommittedPaymentsMock() {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="mock-committed-payments">
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">Pagos · Comprometidos</div>
      <div className="font-display text-base font-semibold text-slate-900 mt-0.5">Cash comprometido (no tuyo)</div>
      <div className="mt-3 space-y-1.5">
        {[
          { c: "Proveedor textiles · 30d", d: "Vence 12 jul", a: "−$ 4.200.000", chip: "30d", chipCls: "bg-emerald-50 text-emerald-700" },
          { c: "Empaques China · 60d", d: "Vence 9 ago", a: "−$ 8.500.000", chip: "60d", chipCls: "bg-amber-50 text-amber-700" },
          { c: "Logística + aduana", d: "Vence 5 sep", a: "−$ 3.100.000", chip: "90d", chipCls: "bg-rose-50 text-rose-700" },
        ].map((r, i) => (
          <div key={i} className="flex items-center justify-between gap-2 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2 text-xs">
            <div className="min-w-0">
              <div className="font-semibold text-slate-900 truncate flex items-center gap-1.5">
                {r.c}
                <span className={`text-[9px] font-bold uppercase rounded-full px-1.5 py-0.5 ${r.chipCls}`}>{r.chip}</span>
              </div>
              <div className="text-[10px] text-slate-500">{r.d}</div>
            </div>
            <span className="font-bold tabular-nums text-rose-700 flex-shrink-0">{r.a}</span>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-2 text-xs">
        <span className="text-slate-500">Total comprometido</span>
        <span className="font-bold text-slate-900 tabular-nums">−$ 15.800.000</span>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────── */
/* ecommerce · GapWeekly                                             */
/* ──────────────────────────────────────────────────────────────── */
export function GapWeeklyMock() {
  const weeks = [
    { w: "S26", net: 1.2, color: "bg-emerald-500" },
    { w: "S27", net: -0.8, color: "bg-rose-500" },
    { w: "S28", net: 0.4, color: "bg-emerald-500" },
    { w: "S29", net: -2.1, color: "bg-rose-600" },
  ];
  return (
    <div className="rounded-2xl border border-rose-200 bg-gradient-to-br from-rose-50/40 to-white p-5" data-testid="mock-gap-weekly">
      <div className="flex items-start gap-2">
        <div className="h-9 w-9 rounded-full bg-rose-100 text-rose-700 flex items-center justify-center flex-shrink-0">
          <AlertTriangle className="h-4 w-4" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-rose-700">Cash gap · semanal</div>
          <div className="font-display text-base font-semibold text-slate-900 mt-0.5">S29 · te quedas corto</div>
        </div>
      </div>
      <div className="mt-3 flex items-end gap-2 h-24">
        {weeks.map((w) => {
          const isNeg = w.net < 0;
          const heightPct = Math.min(100, (Math.abs(w.net) / 2.1) * 100);
          return (
            <div key={w.w} className="flex-1 flex flex-col items-center">
              <div className="relative w-full h-full flex flex-col">
                <div className="flex-1 flex items-end justify-center">
                  {!isNeg && <div className="w-full bg-emerald-400 rounded-t-md" style={{ height: `${heightPct}%` }} />}
                </div>
                <div className="border-t border-slate-300" />
                <div className="flex-1 flex items-start justify-center">
                  {isNeg && <div className="w-full bg-rose-500 rounded-b-md" style={{ height: `${heightPct}%` }} />}
                </div>
              </div>
              <span className="text-[10px] font-mono text-slate-500 mt-1">{w.w}</span>
              <span className={`text-[10px] font-bold tabular-nums ${isNeg ? "text-rose-700" : "text-emerald-700"}`}>{w.net > 0 ? "+" : ""}{w.net}M</span>
            </div>
          );
        })}
      </div>
      <div className="mt-3 text-[11px] text-slate-600 leading-snug bg-rose-50/70 border border-rose-100 rounded-lg px-3 py-2">
        ⚠ S29 cae a −2.1M COP. Movieras un pago de proveedor a S30 y la semana queda en verde.
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────── */
/* extra · CalendarMarks (unused today but reserved)                 */
/* ──────────────────────────────────────────────────────────────── */
export function CalendarMarksMock() {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="mock-calendar-marks">
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">Calendario · Eventos</div>
      <div className="font-display text-base font-semibold text-slate-900 mt-0.5">Marzo 2026</div>
      <div className="mt-3 grid grid-cols-7 gap-1 text-[10px]">
        {["L", "M", "M", "J", "V", "S", "D"].map((d, i) => (
          <div key={i} className="text-center font-bold text-slate-400">{d}</div>
        ))}
        {Array.from({ length: 30 }).map((_, i) => {
          const day = i + 1;
          const isIncome = [5, 12, 22, 28].includes(day);
          const isExpense = [9, 15].includes(day);
          return (
            <div key={day} className={`aspect-square rounded-md flex items-center justify-center text-[10px] font-medium border ${
              isIncome ? "bg-emerald-50 border-emerald-200 text-emerald-700" :
              isExpense ? "bg-rose-50 border-rose-200 text-rose-700" : "border-slate-100 text-slate-500"
            }`}>
              {day}
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex items-center gap-3 text-[10px]">
        <span className="flex items-center gap-1"><span className="h-2 w-2 bg-emerald-400 rounded" /> Ingreso</span>
        <span className="flex items-center gap-1"><span className="h-2 w-2 bg-rose-400 rounded" /> Gasto</span>
      </div>
    </div>
  );
}
